/**
 * Horse Race reducer. Pure function: takes a state + action, returns the next
 * state (or the same reference when nothing changed, so subscribers can skip).
 *
 * Scene routing rules:
 *   - LOBBY (no betting, no track, no podium)     -> lobby
 *   - hr_betting_open                             -> betting
 *   - hr_race_track                               -> track
 *   - hr_race_results                             -> results
 *   - hr_game_over                                -> podium
 *   - WS connection lost                          -> connection_lost
 */

import type { OutMsg } from '@sg/shared';
import type { BrowserState, SceneName } from './types.js';

export type Action =
  | { type: 'ws'; msg: OutMsg }
  | { type: 'connection'; value: BrowserState['connection'] };

export function initialState(): BrowserState {
  return {
    scene: 'lobby',
    connection: 'idle',
    tenantId: '',
    state: 'LOBBY',
    theme: null,
    leaderboard: [],
    leaderboardAllTime: [],
    betting: null,
    countdown: null,
    track: null,
    results: null,
    podium: null,
  };
}

export function reduce(state: BrowserState, action: Action): BrowserState {
  if (action.type === 'connection') {
    if (state.connection === action.value) return state;
    const scene: SceneName = action.value === 'open' ? state.scene : 'connection_lost';
    return { ...state, connection: action.value, scene };
  }
  return reduceWs(state, action.msg);
}

function reduceWs(state: BrowserState, msg: OutMsg): BrowserState {
  switch (msg.type) {
    case 'state_snapshot': {
      const s = msg.state;
      const isLobby = s.state.startsWith('LOBBY');
      const isPodium = s.state.startsWith('PODIUM');
      return {
        ...state,
        tenantId: s.tenant_id,
        state: s.state,
        theme: s.theme ?? state.theme,
        leaderboard: s.leaderboard_top10 ?? state.leaderboard,
        leaderboardAllTime: s.leaderboard_all_time ?? state.leaderboardAllTime,
        betting: isLobby ? null : state.betting,
        countdown: isLobby ? null : state.countdown,
        track: isLobby ? null : state.track,
        results: isLobby ? null : state.results,
        podium: isLobby ? null : state.podium,
        scene: isLobby ? 'lobby' : isPodium ? 'podium' : state.scene,
      };
    }
    case 'hr_betting_open': {
      return {
        ...state,
        scene: 'betting',
        results: null,
        track: null,
        betting: {
          raceId: msg.race_id,
          raceIndex: msg.race_index,
          racesTotal: msg.races_total,
          horses: msg.horses.map((h) => ({
            number: h.number,
            horseId: h.horse_id,
            name: h.name,
            silkColor: h.silk_color,
          })),
          endsAtMs: msg.ends_at_ms,
          countdownStartsAtMs: msg.countdown_starts_at_ms,
          counts: {},
          totalBets: 0,
        },
      };
    }
    case 'hr_bet_tally': {
      if (!state.betting || state.betting.raceId !== msg.race_id) return state;
      return {
        ...state,
        betting: {
          ...state.betting,
          counts: msg.counts,
          totalBets: msg.total_bets,
        },
      };
    }
    case 'hr_race_countdown': {
      // Pre-race countdown overlay phase. Clear any stale track / results so
      // the scene cleanly transitions from betting -> countdown -> track.
      return {
        ...state,
        scene: 'countdown',
        track: null,
        results: null,
        countdown: {
          raceId: msg.race_id,
          startedAtMs: msg.started_at_ms,
          endsAtMs: msg.ends_at_ms,
        },
      };
    }
    case 'hr_race_track': {
      return {
        ...state,
        scene: 'track',
        countdown: null,
        track: {
          raceId: msg.race_id,
          raceIndex: msg.race_index,
          racesTotal: msg.races_total,
          startAtMs: msg.start_at_ms,
          raceDurationMs: msg.race_duration_ms,
          horses: msg.horses.map((h) => ({
            number: h.number,
            horseId: h.horse_id,
            name: h.name,
            silkColor: h.silk_color,
            timeAt25Ms: h.time_at_25_ms,
            timeAt50Ms: h.time_at_50_ms,
            timeAt75Ms: h.time_at_75_ms,
            timeAtFinishMs: h.time_at_finish_ms,
            finishPlace: h.finish_place,
          })),
        },
      };
    }
    case 'hr_race_results': {
      return {
        ...state,
        scene: 'results',
        results: {
          raceId: msg.race_id,
          raceIndex: msg.race_index,
          racesTotal: msg.races_total,
          placings: msg.placings.map((p) => ({
            place: p.place,
            number: p.number,
            horseId: p.horse_id,
            name: p.name,
            silkColor: p.silk_color,
            payout: p.payout,
          })),
          scorers: msg.scorers,
          totalPointsAwarded: msg.total_points_awarded,
        },
      };
    }
    case 'hr_emoji_cheer': {
      // Cheers are pure side-effects rendered by the track scene; no store
      // change. The track scene subscribes to a side-channel via main.ts.
      return state;
    }
    case 'hr_game_over': {
      return {
        ...state,
        scene: 'podium',
        podium: msg.podium,
      };
    }
    default:
      return state;
  }
}
